import React from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

interface CameraModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (e: React.FormEvent) => void;
    formData: {
        id?: number;
        name: string;
        deviceUUID: string;
        macAddress: string;
        ipAddress: string;
        deviceID: string;
        channelID: string;
        role: string;
        status: string;
    };
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    loading: boolean;
    isEdit: boolean;
}

const ROLES = ['ENTRY', 'EXIT'];

export const CameraModal: React.FC<CameraModalProps> = ({
    show,
    onHide,
    onSubmit,
    formData,
    handleInputChange,
    loading,
    isEdit
}) => {
    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title>{isEdit ? 'Edit ANPR Camera' : 'Add New ANPR Camera'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={onSubmit}>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="camName">
                                <Form.Label>Camera Name <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="e.g. Front Gate Entry"
                                    required
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="camUUID">
                                <Form.Label>Device UUID <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="deviceUUID"
                                    value={formData.deviceUUID}
                                    onChange={handleInputChange}
                                    placeholder="Unique hardware UUID"
                                    required
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="camRole">
                                <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    name="role"
                                    value={formData.role}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select Role</option>
                                    {ROLES.map(role => (
                                        <option key={role} value={role}>{role}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="camStatus">
                                <Form.Label>Status <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="ACTIVE">ACTIVE</option>
                                    <option value="INACTIVE">INACTIVE</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </div>

                    <hr className="my-4" />
                    <h6 className="text-muted mb-3">Network & Hardware Details (Optional)</h6>

                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <Form.Group controlId="camIP">
                                <Form.Label>IP Address</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="ipAddress"
                                    value={formData.ipAddress}
                                    onChange={handleInputChange}
                                    placeholder="192.168.x.x"
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-4 mb-3">
                            <Form.Group controlId="camMac">
                                <Form.Label>MAC Address</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="macAddress"
                                    value={formData.macAddress}
                                    onChange={handleInputChange}
                                    placeholder="XX:XX:XX:..."
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-4 mb-3">
                            <Form.Group controlId="camDeviceID">
                                <Form.Label>Hardware Device ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="deviceID"
                                    value={formData.deviceID}
                                    onChange={handleInputChange}
                                    placeholder="Internal ID"
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="camChannel">
                                <Form.Label>Channel ID</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="channelID"
                                    value={formData.channelID}
                                    onChange={handleInputChange}
                                    placeholder="e.g. 1"
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <div className="text-end mt-4">
                        <Button variant="light" onClick={onHide} className="me-2" disabled={loading}>
                            Cancel
                        </Button>
                        <Button variant="info" type="submit" className="text-white" disabled={loading}>
                            {loading ? 'Processing...' : isEdit ? 'Update Camera' : 'Add Camera'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

interface Camera {
    id: number;
    name: string;
    deviceUUID: string;
    macAddress?: string;
    ipAddress?: string;
    deviceID?: string;
    channelID?: number;
    role: string;
    status: string;
}

interface ViewCameraModalProps {
    show: boolean;
    onHide: () => void;
    camera: Camera | null;
}

export const ViewCameraModal: React.FC<ViewCameraModalProps> = ({
    show,
    onHide,
    camera
}) => {
    if (!camera) return null;
    
    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title>ANPR Camera Details</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <div className="row g-4">
                    <div className="col-md-6">
                        <label className="text-muted small d-block">Camera Name</label>
                        <span className="fw-bold fs-5">{camera.name}</span>
                    </div>
                    <div className="col-md-6">
                        <label className="text-muted small d-block">Role</label>
                        <span className={`badge bg-${camera.role === 'ENTRY' ? 'primary' : 'warning'}-subtle text-${camera.role === 'ENTRY' ? 'primary' : 'warning'} px-3`}>
                            {camera.role}
                        </span>
                    </div>
                    
                    <div className="col-md-6 border-top pt-3">
                        <label className="text-muted small d-block">Device UUID</label>
                        <code>{camera.deviceUUID}</code>
                    </div>
                    <div className="col-md-6 border-top pt-3">
                        <label className="text-muted small d-block">Hardware Device ID</label>
                        <span>{camera.deviceID || 'N/A'}</span>
                    </div>
                    
                    <div className="col-md-4 border-top pt-3">
                        <label className="text-muted small d-block">IP Address</label>
                        <span className="text-info">{camera.ipAddress || 'Not set'}</span>
                    </div>
                    <div className="col-md-4 border-top pt-3">
                        <label className="text-muted small d-block">MAC Address</label>
                        <span>{camera.macAddress || 'N/A'}</span>
                    </div>
                    <div className="col-md-4 border-top pt-3">
                        <label className="text-muted small d-block">Channel ID</label>
                        <span>{camera.channelID ?? 'N/A'}</span>
                    </div>
                    
                    <div className="col-12 border-top pt-3">
                        <label className="text-muted small d-block">Operation Status</label>
                        <span className={`badge bg-${camera.status === 'ACTIVE' ? 'success' : 'danger'}-subtle text-${camera.status === 'ACTIVE' ? 'success' : 'danger'}`}>
                            {camera.status}
                        </span>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

interface DeleteModalProps {
    show: boolean;
    onHide: () => void;
    onConfirm: () => void;
    cameraName: string;
    loading: boolean;
}

export const DeleteCameraModal: React.FC<DeleteModalProps> = ({
    show,
    onHide,
    onConfirm,
    cameraName,
    loading
}) => {
    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title>Delete Camera</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center">
                <div className="text-warning mb-4">
                    <i className="ti ti-camera-off fs-1" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5>Confirm Deletion</h5>
                <p className="text-muted">Are you sure you want to delete <strong>{cameraName}</strong>?</p>
            </Modal.Body>
            <Modal.Footer className="border-0 justify-content-center">
                <Button variant="light" onClick={onHide} disabled={loading}>
                    Cancel
                </Button>
                <Button variant="danger" onClick={onConfirm} disabled={loading}>
                    {loading ? 'Deleting...' : 'Yes, Delete It'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
